/* eslint-disable max-len */
import { GridColDef } from '@mui/x-data-grid-pro';
import { useFormikContext } from 'formik';
import { ReactElement } from 'react';
import { PanelType } from 'src/types/PanelType';
import { useDirtyState } from '../../../pages/DataInput/Student/GradesAndPromotions/GradesAndPromotionsInput';
import { useAppSelector } from '../../../redux/hooks';
import { selectGradesAndPromotionsFormViewModel } from '../../../redux/slices/gradesAndPromotionsSlice';
import {
  CourseModel,
  GradesAndPromotionsModel,
} from '../../../types/GradesAndPromotionsModel';
import Panel from '../../Panel/Panel';
import StyledGrid from '../../StyledGrid/StyledGrid';

const GradesAndPromotions_UpdateGradesPanel = (): ReactElement => {
  // Use Formik context to access values and setFieldValue
  const { values, setFieldValue } =
    useFormikContext<GradesAndPromotionsModel>();
  const formViewModel = useAppSelector(selectGradesAndPromotionsFormViewModel);
  const { setUserHasMadeChanges } = useDirtyState();
  // Use updatedCourses from Formik values
  const courses = values.courses || [];
  const handleRowUpdate = (
    newRow: CourseModel,
    oldRow: CourseModel
  ): CourseModel => {
    const updatedRows = courses.map((row: CourseModel) =>
      row.courseSectionIdentifier === oldRow.courseSectionIdentifier
        ? newRow
        : row
    );
    setFieldValue('courses', updatedRows);
    newRow.isModified = true;
    setUserHasMadeChanges(true);
    return newRow;
  };

  const columns: GridColDef[] = [
    {
      field: 'sessionType',
      headerName: 'Session',
      flex: 1,
      minWidth: 120,
      editable: false,
    },
    {
      field: 'courseSectionIdentifier',
      headerName: 'Course Section Identifier',
      flex: 1.5,
      minWidth: 180,
      editable: false,
    },
    {
      field: 'courseTitle',
      headerName: 'Course Title',
      description: 'Title of the course',
      flex: 2,
      minWidth: 200,
      editable: false,
    },
    {
      field: 'gradeEarned',
      headerName: 'Final Grade',
      flex: 1,
      minWidth: 120,
      editable: values.studentUpdate,
    },
    {
      field: 'numberOfCreditsEarned',
      headerName: 'Carnegie Unit',
      flex: 1,
      minWidth: 120,
      editable: values.studentUpdate,
    },
    {
      field: 'updatedByReason',
      headerName: 'Reason for Update',
      flex: 1.5,
      minWidth: 150,
      editable: values.studentUpdate,
      type: 'singleSelect',
      valueOptions:
        formViewModel?.updateReasonOptions?.map((x) => x.value) ?? [],
    },
    {
      field: 'updatedByName',
      headerName: 'Updated By',
      description: 'User who updated the record',
      flex: 1,
      minWidth: 120,
      editable: false,
    },
    {
      field: 'updatedDate',
      headerName: 'Updated Date',
      description: 'Date when the record was updated',
      flex: 1,
      minWidth: 150,
      editable: false,
    },
  ];

  return (
    <Panel
      panelType={PanelType.INFO}
      heading="Update Grades"
      className="dynamic-panel"
    >
      <div className="update-grades-grid-container">
        <div style={{ marginBottom: '16px', fontSize: '14px', color: '#666' }}>
          <p style={{ margin: '0 0 8px 0' }}>
            To adjust the Final Grade or Carnegie unit, click in the cell to
            edit the value.
          </p>
          <p style={{ margin: '0' }}>
            To adjust the Reason for Update, use the drop-down menu to select
            the appropriate value.
          </p>
        </div>
        <StyledGrid
          columns={columns}
          rows={courses}
          getRowId={(row: CourseModel) => {
            // Create a unique ID from available fields
            return `${row.courseSectionIdentifier}`;
          }}
          editMode="row"
          hideFooter
          processRowUpdate={handleRowUpdate}
        />
      </div>
    </Panel>
  );
};

export default GradesAndPromotions_UpdateGradesPanel;
